/**
 * Redact secret-bearing tokens from any URL before it reaches Sentry (#2101 F3).
 * Shared by sentry.server/client/edge.config so the three configs cannot drift.
 *
 * Vectors covered:
 *  - the emailed magic-link PAGE path, where the token is a path segment
 *    (/family-invitations/accept/<token> and its four siblings) — captured by
 *    pageload transactions and error events on the CLIENT before the page's
 *    history.replaceState scrub runs, and mirrored in the server and edge
 *    Sentry configs;
 *  - any credential-shaped query or fragment param (SECRET_QUERY_PARAMS),
 *    including the URL-encoded nesting produced by `?redirectTo=%2F…%3Ftoken%3D…`.
 *
 * Applied via beforeSend + beforeSendTransaction + beforeBreadcrumb in every
 * runtime (server / client / edge).
 *
 * SESSION REPLAY is handled SEPARATELY and cannot be fixed by those three hooks.
 * Read in the installed @sentry-internal/replay 10.44.0 build:
 *  - `setInitialState()` composes `WINDOW.location.pathname + hash + search`
 *    into `_context.initialUrl` / `_context.urls` at recording start
 *    (cjs/index.js:9105-9118) — note it includes BOTH the fragment and the
 *    query string, so `?token=` and `#access_token=` both land;
 *  - slow-click / multi-click breadcrumbs read `WINDOW.location.href`
 *    (index.js:4884, 4907) and go into the rrweb RECORDING payload via
 *    `addBreadcrumbEvent` (index.js:4595);
 *  - SPA navigations push the full destination URL into `_context.urls` via
 *    `handleHistorySpanListener` (index.js:6866-6890);
 *  - `sendReplayRequest` copies `urls` onto the `replay_event` envelope
 *    (index.js:8190, from `_popEventContext` :9462), built by `prepareReplayEvent` -> `core.prepareEvent`
 *    (index.js:8120) rather than the client's `_processEvent` — the SDK says so
 *    in its own comment at index.js:8136-8138.
 *
 * A `Sentry.addEventProcessor` WOULD reach the `replay_event` (core.prepareEvent
 * runs the event processors), but the `replay_recording` envelope item passes
 * through no processor at all, so the slow-click href and the navigation spans
 * are unreachable by any hook. Not recording a token-bearing URL is therefore
 * the only complete fix: see `isReplayBlockedUrl` below, used by
 * src/instrumentation-client.ts at init and by SentryReplayGuard on every navigation.
 */

/**
 * Query- or fragment-parameter names whose VALUE is a live credential in a
 * CheerVue URL. One list, read by the Replay gate AND the URL scrubber so the
 * two cannot drift, and swept against source by
 * `__tests__/replayTokenPageCoverage.test.ts`.
 *
 * ORDER IS NOT LOAD-BEARING: the alternation backtracks either way, because `=`
 * must follow the name. Each entry carries the reader that makes it a secret.
 */
export const SECRET_QUERY_PARAMS = [
  'invitationToken',
  'enrollment_ids', // public /enrollment/success — anon read via /api/public/programs/enrollments/<id>
  'refresh_token',
  'access_token',
  'enrollment_id',
  'inviteProof', // signed proof of a family-invite email (src/app/register/page.tsx)
  'session_id', // Stripe checkout session — /api/public/stripe/session-info/<id> returns the full receipt
  'token_hash',
  'invoice_id', // public /enrollment/success — anon read via …/enrollments/by-invoice/<id>
  'handoff', // single-use code the mobile app mints for a WebView session
  'refresh', // the mobile WebView's REFRESH token (change-password-webview.tsx)
  'userId', // public /report-unauthorized-change — the id alone lets a stranger file a report on that user
  'token',
  'code', // Supabase PKCE code on the root page; team join code on its validate route
  't', // signed waitlist claim token in the offer email (offerNextInLine.ts)
  'payment_intent_client_secret', // Stripe's return to /t/<token> after a card check — lets the holder read the payment
] as const;

/**
 * FAIL-CLOSED (#4704): every query param a browser page reads out of a URL must
 * appear in exactly one of this list or `BENIGN_URL_PARAMS`
 * (./urlParamRegistry.ts). The guard test sweeps source for every name and
 * goes red on an unclassified one, naming the file that reads it — so a new
 * credential can no longer slip in because nobody thought its NAME looked
 * secret (`refresh`, `t`, `inviteProof`, `handoff`, `session_id` all did).
 *
 * The rule for which list: **could a stranger USE the value from the address
 * bar?** A signed token, a session credential, or an id that a public route
 * answers with no login (an enrollment, a Stripe receipt, a tryout registration
 * with the parent's phone) is secret. A filter, a tab name, a slug, or an id
 * that only means something behind a login is benign.
 */

/**
 * Page routes whose DYNAMIC PATH SEGMENT is itself the credential.
 *
 * `*` matches exactly one path segment. Route groups like `(public)` are not
 * part of the URL, so they do not appear.
 *
 * Two kinds of route live here. The `[token]` ones say what they are in the
 * directory name. The last two do NOT: their segment is an ID that a public,
 * no-login page treats as the only access control — the reveal route's own API
 * says "teamAthleteId is the token" (src/app/api/team-placement-reveal/
 * [teamAthleteId]/response/route.ts:9).
 *
 * FAIL-CLOSED (#4704): every dynamic page route under src/app must appear in
 * exactly one of this list or `BENIGN_DYNAMIC_PAGE_ROUTES`
 * (./urlParamRegistry.ts). The guard test derives the route set from the
 * filesystem and goes red on an unclassified one, so an ID-named token route
 * can no longer hide behind an innocent directory name.
 */
export const TOKEN_PATH_ROUTE_PATTERNS = [
  '/family-invitations/accept/*',
  '/parent-invitations/accept/*',
  '/pay/*',
  '/invoice-pay/*',
  '/store/*/order/*',
  // Event tickets (KTD7): the leaf is the order's token; it admits people at the door.
  '/t/*',
  '/team-placement-reveal/*',
  '/placement-response/*',
  // #4217: a gym's join link. Meant to be shared (posters, social posts), but a
  // stranger holding it can still join that gym as a Lead, so it is a token.
  '/join/*',
  // Same consistency rule: the leaf is the auth user id that `?userId=` on the
  // public /report-unauthorized-change page turns into a forged critical
  // security alert. Weak capability (needs Sentry access, discloses nothing
  // back), but the registry must say one thing about one value.
  '/users/*',
] as const;

/**
 * The API twins. Replay never sees these (they are fetch targets, not page
 * URLs), but a token page fetches them on mount, and a fetch BREADCRUMB's
 * `data.url` rides every error event — so the scrubber must redact them or the
 * token leaks through the ordinary error path instead. Same `*` convention.
 *
 * FAIL-CLOSED (#4704): every dynamic route under src/app/api that
 * src/proxy.ts makes public must appear in exactly one of this list or
 * `BENIGN_PUBLIC_API_ROUTES` (./urlParamRegistry.ts); the guard test derives
 * the public set from the middleware allow-lists plus the filesystem.
 *
 * Both enrollments patterns are listed; the credential segment must contain a
 * digit or a capital (see TOKEN_SEGMENT), so the literal `by-invoice` segment
 * is left readable and only the id after it is redacted.
 */
export const TOKEN_API_ROUTE_PATTERNS = [
  '/api/public/invoice-pay/*',
  '/api/public/pos/payment-link/*',
  '/api/public/store/order/*',
  // Event tickets (KTD7): order, confirm, and QR image all carry the token.
  '/api/public/tickets/*',
  '/api/public/stripe/session-info/*',
  '/api/public/programs/enrollments/by-invoice/*',
  '/api/public/programs/enrollments/*',
  '/api/public/tryout-events/session/*',
  '/api/team-placement-reveal/*',
  // #4217: the join link's four steps carry its token.
  '/api/public/join/*/start',
  '/api/public/join/*/send-code',
  '/api/join/*/athletes',
  '/api/join/*/complete',
  // NOT public per src/proxy.ts (a stranger gets 401), but the public
  // /placement-response/<id> page fetches it with the capability id, so the
  // breadcrumb still has to be redacted. Defence-in-depth entries are allowed
  // here; the guard test only requires that the route exist on disk.
  '/api/tryout-registrations/*',
] as const;

/**
 * A literal segment, escaped, with every letter as a `[Pp]`-style class. That
 * makes the ROUTE case-insensitive (`/Pay/<token>` must redact exactly like
 * `/pay/<token>` — the gate blocks it, so the scrubber must scrub it) without
 * the regex `i` flag, which would also make TOKEN_SEGMENT's `[0-9A-Z]` match
 * lowercase and silently void the literal-sibling protection.
 */
const escapeSegment = (segment: string) =>
  segment
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/[a-z]/gi, (ch) => `[${ch.toUpperCase()}${ch.toLowerCase()}]`);

/** `/pay/*` -> `^\/pay\/[^/]+` — prefix-anchored, so deeper URLs still match. */
function routePatternSource(pattern: string): string {
  return pattern
    .split('/')
    .filter(Boolean)
    .map((segment) => `/${segment === '*' ? '[^/]+' : escapeSegment(segment)}`)
    .join('');
}

const TOKEN_PATH_RES = TOKEN_PATH_ROUTE_PATTERNS.map(
  (pattern) => new RegExp(`^${routePatternSource(pattern)}`)
);

/**
 * The same routes, UNANCHORED — for finding a token route nested inside another
 * URL, e.g. `?redirectTo=%2Fpay%2F<token>` once decoded. `useRequireAuth` builds
 * that shape from whatever page the user was on, so it is not route-specific.
 */
const TOKEN_PATH_CONTAINS_RES = TOKEN_PATH_ROUTE_PATTERNS.map(
  (pattern) => new RegExp(routePatternSource(pattern))
);

/**
 * A token route may appear at the start of a string, after whitespace, a quote,
 * an `=` (a literal `redirectTo=/pay/<token>`), or after a scheme+host. It must
 * NOT match mid-path: `/pay/*` is short and generic, and without this boundary
 * `/api/billing/pay/confirm` would be treated as a token URL.
 */
const ROUTE_BOUNDARY = `(^|[\\s"'(<=]|https?://[^\\s/]+)`;
/**
 * The credential segment. Excludes whitespace and quotes as well as URL
 * delimiters: these regexes also run over breadcrumb MESSAGES and console
 * arguments, where `[^/?#]+` would swallow the rest of an English sentence
 * ("Navigated from /pay/settings to /pay/history" lost "settings to ").
 *
 * It must also LOOK like a credential: contain a digit or a capital letter, or
 * be at least 20 characters long. Every credential CheerVue puts in a path —
 * hex HMACs (64), base64url tokens (43), UUIDs (36), Stripe ids — satisfies
 * both; a literal sibling segment like `by-invoice`, `my-registrations` or
 * `list` satisfies neither, so those stay readable in Sentry instead of
 * becoming `[redacted]/[redacted]`. The length arm is what keeps a degenerate
 * all-lowercase test token covered. This applies to REDACTION only; the Replay
 * gate still blocks on the route shape alone (over-blocking is the safe
 * direction), and a future literal sibling of 20+ characters would merely be
 * over-redacted.
 */
const SEGMENT_CHARS = `[^/?#\\s"'<>]`;
const LOOKS_LIKE_A_CREDENTIAL = `(?=${SEGMENT_CHARS}*[0-9A-Z]|${SEGMENT_CHARS}{20})`;
const TOKEN_SEGMENT = `${LOOKS_LIKE_A_CREDENTIAL}${SEGMENT_CHARS}+`;

/**
 * Redact only the FINAL segment of each token route — the credential. Earlier
 * wildcards (the store's gym slug) are not secret and stay readable.
 */
const REDACTED_ROUTE_PATTERNS = [...TOKEN_PATH_ROUTE_PATTERNS, ...TOKEN_API_ROUTE_PATTERNS];

const TOKEN_PATH_REDACT_RES = REDACTED_ROUTE_PATTERNS.map(
  (pattern) =>
    new RegExp(
      `${ROUTE_BOUNDARY}(${routePatternSource(
        pattern.slice(0, pattern.lastIndexOf('/*'))
      )}/)${TOKEN_SEGMENT}`,
      // 'g' only: with 'i', the digit-or-capital lookahead's [A-Z] would match
      // lowercase and the literal-sibling protection would silently vanish.
      // Case-insensitivity comes from escapeSegment's letter classes instead.
      'g'
    )
);

/**
 * The percent-encoded path form, produced by `encodeURIComponent` inside a
 * `redirectTo=` value (src/app/parent-invitations/accept/[token]/page.tsx and
 * src/hooks/auth/useRequireAuth.ts). The literal `%2F` is upper-case only —
 * lowercase `%2f` is covered because scrubInviteTokenFromUrl decodes and
 * re-scrubs. Do NOT restore the `i` flag to get it: that would also make the
 * digit-or-capital lookahead match lowercase and void the literal-sibling rule.
 */
const TOKEN_PATH_ENCODED_REDACT_RES = REDACTED_ROUTE_PATTERNS.map(
  (pattern) =>
    new RegExp(
      `(${routePatternSource(pattern.slice(0, pattern.lastIndexOf('/*'))).replace(
        /\//g,
        '%2F'
      )}%2F)(?=[^&#%\\s"'<>]*[0-9A-Z]|[^&#%\\s"'<>]{20})[^&#%\\s"'<>]+`,
      'g'
    )
);

const SECRET_NAMES = SECRET_QUERY_PARAMS.join('|');

/**
 * Separator and `=` are each accepted literal OR percent-encoded, in any
 * combination. That single alternation is what keeps the GATE and the SCRUBBER
 * matching the same set — an earlier version had a literal regex and a separate
 * fully-encoded one, and a half-encoded pair (`%3Ftoken=`) matched neither.
 *
 * Shapes covered:
 *   ?token=…  &token=…  #access_token=…      (plain query and fragment)
 *   %3Ftoken%3D…  %26token%3D…               (encodeURIComponent'd redirectTo)
 *   %3Ftoken=…  ?token%3D…                   (mixed)
 */
const PARAM_SEPARATOR = '(?:[?&#]|%3F|%26|%23)';
const PARAM_EQUALS = '(?:=|%3D)';
/**
 * A value ends at the next separator, literal or encoded, and is non-empty. It
 * also stops at whitespace and quotes: like TOKEN_SEGMENT, this runs over
 * breadcrumb MESSAGES, where an open-ended class ate the rest of the sentence
 * ("…?token=<t> failed with 500 after 3 retries" lost everything after the
 * token).
 */
const PARAM_VALUE = `(?:(?!%26|%23)[^&#\\s"'<>])+`;

const SECRET_PARAM_RE = new RegExp(
  `${PARAM_SEPARATOR}(?:${SECRET_NAMES})${PARAM_EQUALS}${PARAM_VALUE}`,
  'i'
);

/** Same, capturing the `?name=` prefix so the scrubber can keep it. */
const SECRET_PARAM_REDACT_RE = new RegExp(
  `(${PARAM_SEPARATOR}(?:${SECRET_NAMES})${PARAM_EQUALS})${PARAM_VALUE}`,
  'gi'
);

/**
 * Decode until stable (double-encoding exists in the wild), capped so a crafted
 * input cannot spin. decodeURIComponent throws on a malformed `%` — never let
 * that break a hook that runs on every event.
 */
function safeDecode(value: string): string {
  let current = value;
  for (let pass = 0; pass < 3; pass += 1) {
    let next: string;
    try {
      next = decodeURIComponent(current);
    } catch {
      return current;
    }
    if (next === current) return current;
    current = next;
  }
  return current;
}

/**
 * One URL part (`search` or `hash`), with or without its leading separator, and
 * also percent-decoded so a token nested inside a `redirectTo=` value cannot
 * slip through.
 */
function hasCredential(raw: string, separator: '?' | '#'): boolean {
  const part = raw || '';
  if (!part || part === separator) return false;
  const normalized = part.startsWith(separator) ? part : `${separator}${part}`;
  const decoded = safeDecode(normalized);
  return [normalized, decoded].some(
    (candidate) =>
      SECRET_PARAM_RE.test(candidate) ||
      // A whole token ROUTE nested inside another param — `useRequireAuth`
      // builds `?redirectTo=${encodeURIComponent('/pay/<token>')}` from
      // whatever page the user was on, so this is not route-specific.
      TOKEN_PATH_CONTAINS_RES.some((re) => re.test(candidate))
  );
}

/**
 * Does this URL carry a live token that Session Replay would ship to Sentry?
 *
 * Deliberately SHAPE-based rather than a list of page names: Replay captures the
 * URL outside every scrubbing hook (see the note at the top of this file), so a
 * new credential-bearing page added later must be blocked by its shape, not by
 * somebody remembering to extend a route allow-list. Three shapes exist today:
 *
 *  - the credential is a path segment (`/pay/<token>`) — TOKEN_PATH_ROUTE_PATTERNS;
 *  - the credential is a query value (`/support-thread?token=…`);
 *  - the credential is a fragment value (`/signin#access_token=…&refresh_token=…`).
 *
 * `search` and `hash` are each accepted with or without their leading `?` / `#`,
 * and are also tested percent-decoded so a token nested inside a `redirectTo=`
 * value cannot slip through.
 *
 * Matches only when a non-empty value is actually present, so the post-scrub
 * `/family-invitations/accept` (no token) is NOT excluded from recording.
 */
export function isReplayBlockedUrl(pathname: string, search: string, hash = ''): boolean {
  if (TOKEN_PATH_RES.some((re) => re.test(pathname || ''))) return true;
  return hasCredential(search, '?') || hasCredential(hash, '#');
}

function scrubOnce(url: string): string {
  let scrubbed = url;
  for (const redactRe of TOKEN_PATH_REDACT_RES) {
    // $1 is the boundary (start / whitespace / quote / `=` / scheme+host), $2
    // the route prefix; only the credential segment after them is dropped.
    scrubbed = scrubbed.replace(redactRe, '$1$2[redacted]');
  }
  for (const redactRe of TOKEN_PATH_ENCODED_REDACT_RES) {
    scrubbed = scrubbed.replace(redactRe, '$1[redacted]');
  }
  return scrubbed.replace(SECRET_PARAM_REDACT_RE, '$1[redacted]');
}

/**
 * The gate decodes before testing; the scrubber must see the same URL the gate
 * saw, or a URL can be blocked from Replay while its token still ships on the
 * error event. So: scrub the raw string, then decode and scrub again. If the
 * second pass found something the first could not (a doubly-encoded or a
 * literal-slash nesting), return the DECODED, scrubbed form — readability in
 * Sentry beats byte-fidelity to a URL that carried a credential.
 */
export function scrubInviteTokenFromUrl(url: string): string {
  if (!url) return url;
  const scrubbed = scrubOnce(url);
  const decoded = safeDecode(scrubbed);
  if (decoded === scrubbed) return scrubbed;
  const scrubbedDecoded = scrubOnce(decoded);
  return scrubbedDecoded === decoded ? scrubbed : scrubbedDecoded;
}

/**
 * Scrub the token out of an event's request URL and transaction name in place.
 * Generic over the minimal Sentry event shape so it works as both a `beforeSend`
 * and a `beforeSendTransaction` hook across all three runtimes.
 */
export function scrubInviteTokenFromEvent<
  T extends { request?: { url?: string; headers?: Record<string, string> } | null; transaction?: string }
>(event: T): T {
  if (event.request?.url) {
    event.request.url = scrubInviteTokenFromUrl(event.request.url);
  }
  // The marketing proxy's shared secret rides on every proxied public-auth request.
  const headers = event.request?.headers;
  if (headers) {
    for (const name of Object.keys(headers)) {
      if (name.toLowerCase() === 'x-cheervue-proxy-secret') delete headers[name];
    }
  }
  if (event.transaction) {
    event.transaction = scrubInviteTokenFromUrl(event.transaction);
  }
  return event;
}

/**
 * Scrub the token out of a BREADCRUMB. Required in addition to the event hook:
 * the accept page's `history.replaceState` only runs after the validate fetch
 * resolves, so Sentry records a `navigation` breadcrumb whose `from` is still
 * the token URL — a field `beforeSend` does not touch.
 *
 * Covers navigation (`data.from` / `data.to`), fetch/xhr (`data.url`), and any
 * URL embedded in the breadcrumb message.
 */
export function scrubInviteTokenFromBreadcrumb<
  T extends { message?: string; data?: Record<string, unknown> | null }
>(breadcrumb: T): T {
  // typeof-guard the message the same way the data loop is guarded: Sentry types
  // `message` as string, but a non-string slipping through would throw inside a
  // hook that runs on EVERY breadcrumb (the handler try/catch would then swallow
  // that breadcrumb entirely). Round-3 review nice-to-have.
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = scrubInviteTokenFromUrl(breadcrumb.message);
  }
  const data = breadcrumb.data;
  if (data) {
    for (const key of ['from', 'to', 'url'] as const) {
      const value = data[key];
      if (typeof value === 'string') {
        data[key] = scrubInviteTokenFromUrl(value);
      }
    }
    // Console breadcrumbs carry the logged values in `data.arguments`; a token
    // logged there would otherwise survive. Round-3 review nice-to-have.
    if (Array.isArray(data.arguments)) {
      data.arguments = (data.arguments as unknown[]).map((arg) =>
        typeof arg === 'string' ? scrubInviteTokenFromUrl(arg) : arg
      );
    }
  }
  return breadcrumb;
}
